/**
 * Canonical design tokens. This is the single source of truth for colour,
 * radius and spacing across the product.
 *
 * Consumed by:
 *  - src/plugins/vuetify.ts (the Vuetify theme)
 *  - chart configs (ECharts/ApexCharts/Chart.js options)
 *  - anywhere else JS needs a colour value
 *
 * Mirrored in src/assets/tokens.css as a Tailwind v4 @theme block, so the
 * same values are available as Tailwind utilities (bg-surface-card,
 * text-ink-muted, etc). Keep the two in step — see tokens.spec.ts, which
 * fails the build if they drift.
 *
 * Accent has two roles, deliberately kept distinct:
 *  - `accent` (#0E7490, deep cyan) drives every interactive element: buttons,
 *    links, focus rings, the primary chart series. 5.36:1 on white — passes
 *    WCAG AA.
 *  - `brandCyan` (#00E5FF, electric cyan) is the brand mark. It is legible
 *    only on near-black surfaces (12.9:1 on #0A0A0A) and must never be used
 *    for text or controls on a light surface — at ~1.5:1 on white it fails
 *    contrast outright. Restrict it to dark hero/CTA bands.
 */
export const tokens = {
  // Surfaces
  surfacePage: '#F7F8F9',
  surfaceCard: '#FFFFFF',
  surfaceSubtle: '#F1F3F5',

  // Rules — hairlines, never shadows
  border: '#E3E5E8',
  borderStrong: '#CDD1D6',

  // Ink
  ink: '#111417', // primary text, big numbers
  inkMuted: '#5C6470', // labels, units — 7.0:1 on white
  inkFaint: '#8A929E', // axis ticks, disabled — non-text use only

  // Interactive accent — the ONE accent for controls and data
  accent: '#0E7490',
  accentHover: '#0B5C73',
  accentSubtle: '#E6F4F8',

  // Brand mark — dark surfaces only, see note above
  brandCyan: '#00E5FF',

  // Status
  positive: '#0F7B4F',
  negative: '#C0392B',
  caution: '#A16207',

  // Shape
  radius: '8px',
  radiusSm: '4px',
} as const

export type DesignTokens = typeof tokens
